import React, { useMemo, useState } from 'react';
import { Card, DatePicker, Empty, Row, Col, Select, Spin, Typography } from 'antd';
import {
  PieChartOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import PageTitle from '@components/common/PageTitle';
import HeaderRow from '@components/common/HeaderRow';
import ErrorFallback from '@components/common/ErrorFallback';
import DonutChart from '@components/common/charts/DonutChart';
import AreaChart from '@components/common/charts/AreaChart';
import { useVisaApplicationAnalytics } from '../hooks/use-visa-application-analytics.hook';
import type { VisaAnalyticsData } from '../types/visa-application-analytics.types';
import { VisaStatusEnum } from '@components/dashboard/admin/visa-applications/types/visa-application.types';
import { useActiveTenant } from '@states/active-tenant.atom';
import { useTranslation } from 'react-i18next';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const VisaAnalyticsContainer: React.FC = () => {
  const { t } = useTranslation();
  const { activeTenant } = useActiveTenant();

  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<VisaStatusEnum[]>([]);

  const [startDate, endDate] = useMemo(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      return [undefined, undefined] as const;
    }
    return [
      dateRange[0]?.startOf('day').format('YYYY-MM-DD'),
      dateRange[1]?.endOf('day').format('YYYY-MM-DD'),
    ] as const;
  }, [dateRange]);

  const {
    data: analyticsResponse,
    isLoading,
    error,
  } = useVisaApplicationAnalytics(
    {
      startDate,
      endDate,
      visaStatus: selectedStatuses,
    },
    {
      enabled: !!activeTenant?.id,
    }
  );

  const analyticsData: VisaAnalyticsData | undefined = analyticsResponse?.data;

  const statusDistributionData = useMemo(
    () =>
      analyticsData?.statusDistribution?.map((item) => ({
        category: item.status,
        value: item.count,
      })) ?? [],
    [analyticsData]
  );

  const statusTrendData = useMemo(
    () =>
      analyticsData?.statusTrend?.map((item) => ({
        date: item.date,
        category: item.status,
        value: item.count,
      })) ?? [],
    [analyticsData]
  );

  const statusSummary = analyticsData?.statusSummary;

  const hasAnyData = statusDistributionData.length > 0 || statusTrendData.length > 0;

  const statusOptions = useMemo(
    () =>
      Object.values(VisaStatusEnum).map((value) => ({
        label: value.replace(/_/g, ' '),
        value,
      })),
    []
  );

  const handleDateRangeChange = (values: null | (Dayjs | null)[]) => {
    if (!values || values.length !== 2) {
      setDateRange(null);
      return;
    }

    const [start, end] = values;
    if (!start || !end) {
      setDateRange(null);
      return;
    }

    setDateRange([start, end]);
  };

  return (
    <>
      <PageTitle title={t('visaAnalytics.title', 'Visa Application Analytics')} />
      <HeaderRow
        title={t('visaAnalytics.title', 'Visa Application Analytics')}
        showBackButton={false}
        showCreateButton={false}
        showBreadcrumbs={true}
      />

      <div className="row mt-3">
        <div className="col-12">
          {/* Filters card */}
          <Card className="shadow-lg" style={{ marginBottom: 24 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Text strong>{t('visaAnalytics.filters.dateRange', 'Date range')}</Text>
                <div style={{ marginTop: 8 }}>
                  <RangePicker
                    style={{ width: '100%' }}
                    value={dateRange ? [dateRange[0] as Dayjs, dateRange[1] as Dayjs] : null}
                    onChange={handleDateRangeChange}
                    disabledDate={(current) => !!current && current.isAfter(dayjs().endOf('day'))}
                  />
                </div>
              </Col>
              <Col xs={24} md={12}>
                <Text strong>{t('visaAnalytics.filters.status', 'Visa status')}</Text>
                <div style={{ marginTop: 8 }}>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    placeholder={t(
                      'visaAnalytics.filters.statusPlaceholder',
                      'Select visa statuses'
                    )}
                    options={statusOptions}
                    value={selectedStatuses}
                    onChange={(values: VisaStatusEnum[]) => setSelectedStatuses(values)}
                    maxTagCount="responsive"
                  />
                </div>
              </Col>
            </Row>
          </Card>

          {/* Content / charts */}
          <Spin spinning={isLoading}>
            {error && (
              <div className="mb-3">
                <ErrorFallback
                  error={error}
                  details={t(
                    'visaAnalytics.error.generic',
                    'Failed to load visa analytics. Please try again.'
                  )}
                  resetErrorBoundary={() => {}}
                />
              </div>
            )}

            {!error && !isLoading && !hasAnyData && (
              <Card className="shadow-lg">
                <Empty
                  description={t(
                    'visaAnalytics.empty.noData',
                    'No analytics data available for the selected filters.'
                  )}
                />
              </Card>
            )}

            {hasAnyData && (
              <>
                {statusSummary && (
                  <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                    <Col xs={12} md={6} lg={4}>
                      <Card className="shadow-lg" size="small">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ fontSize: 24, color: '#1890ff' }}>
                            <PieChartOutlined />
                          </div>
                          <div>
                            <Text type="secondary">
                              {t('visaAnalytics.summary.total', 'Total applications')}
                            </Text>
                            <Title level={4} style={{ margin: 0 }}>
                              {statusSummary.total}
                            </Title>
                          </div>
                        </div>
                      </Card>
                    </Col>
                    <Col xs={12} md={6} lg={4}>
                      <Card className="shadow-lg" size="small">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ fontSize: 24, color: '#faad14' }}>
                            <ClockCircleOutlined />
                          </div>
                          <div>
                            <Text type="secondary">
                              {t('visaAnalytics.summary.pending', 'Pending')}
                            </Text>
                            <Title level={4} style={{ margin: 0 }}>
                              {statusSummary.pending}
                            </Title>
                          </div>
                        </div>
                      </Card>
                    </Col>
                    <Col xs={12} md={6} lg={4}>
                      <Card className="shadow-lg" size="small">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ fontSize: 24, color: '#1890ff' }}>
                            <SyncOutlined />
                          </div>
                          <div>
                            <Text type="secondary">
                              {t('visaAnalytics.summary.inProgress', 'In progress')}
                            </Text>
                            <Title level={4} style={{ margin: 0 }}>
                              {statusSummary.in_progress}
                            </Title>
                          </div>
                        </div>
                      </Card>
                    </Col>
                    <Col xs={12} md={6} lg={4}>
                      <Card className="shadow-lg" size="small">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ fontSize: 24, color: '#52c41a' }}>
                            <CheckCircleOutlined />
                          </div>
                          <div>
                            <Text type="secondary">
                              {t('visaAnalytics.summary.processed', 'Processed')}
                            </Text>
                            <Title level={4} style={{ margin: 0 }}>
                              {statusSummary.processed}
                            </Title>
                          </div>
                        </div>
                      </Card>
                    </Col>
                    <Col xs={12} md={6} lg={4}>
                      <Card className="shadow-lg" size="small">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ fontSize: 24, color: '#ff4d4f' }}>
                            <CloseCircleOutlined />
                          </div>
                          <div>
                            <Text type="secondary">
                              {t('visaAnalytics.summary.failed', 'Failed')}
                            </Text>
                            <Title level={4} style={{ margin: 0 }}>
                              {statusSummary.failed}
                            </Title>
                          </div>
                        </div>
                      </Card>
                    </Col>
                    <Col xs={12} md={6} lg={4}>
                      <Card className="shadow-lg" size="small">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ fontSize: 24, color: '#722ed1' }}>
                            <TrophyOutlined />
                          </div>
                          <div>
                            <Text type="secondary">
                              {t('visaAnalytics.summary.successRate', 'Success rate')}
                            </Text>
                            <Title level={4} style={{ margin: 0 }}>
                              {statusSummary.successRate.toFixed(1)}%
                            </Title>
                          </div>
                        </div>
                      </Card>
                    </Col>
                  </Row>
                )}

                <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                  <Col xs={24} lg={8}>
                    <Card className="shadow-lg">
                      <Title level={4}>
                        {t('visaAnalytics.charts.statusDistribution', 'Status distribution')}
                      </Title>
                      <DonutChart data={statusDistributionData} />
                    </Card>
                  </Col>
                  <Col xs={24} lg={16}>
                    <Card className="shadow-lg">
                      <Title level={4}>
                        {t('visaAnalytics.charts.statusTrend', 'Status distribution over time')}
                      </Title>
                      <AreaChart data={statusTrendData} />
                    </Card>
                  </Col>
                </Row>
              </>
            )}
          </Spin>
        </div>
      </div>
    </>
  );
};

export default VisaAnalyticsContainer;

